import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { HeroChatDemo } from "@/components/landing/HeroChatDemo";

export const HeroSection: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <section
      id="home"
      className="relative bg-tiber-900 text-white overflow-hidden min-h-[92vh] flex items-center"
    >
      {/* Decorative elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 right-20 w-72 h-72 bg-white rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-tiber-500 rounded-full blur-3xl"></div>
      </div>

      {/* Animated white lines background */}
      <div className="absolute inset-0 opacity-20 overflow-hidden">
        {/* Horizontal lines */}
        <div className="absolute top-1/4 left-0 w-full h-px bg-linear-to-r from-transparent via-white/30 to-transparent animate-pulse"></div>
        <div
          className="absolute top-1/2 left-0 w-full h-px bg-linear-to-r from-transparent via-white/20 to-transparent animate-pulse"
          style={{ animationDelay: "1s" }}
        ></div>
        <div
          className="absolute top-3/4 left-0 w-full h-px bg-linear-to-r from-transparent via-white/25 to-transparent animate-pulse"
          style={{ animationDelay: "2s" }}
        ></div>

        {/* Diagonal lines */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div
            className="absolute top-1/3 left-0 w-[200%] h-px bg-linear-to-r from-transparent via-white/20 to-transparent transform rotate-12 origin-left"
            style={{ animation: "slide 20s linear infinite" }}
          ></div>
          <div
            className="absolute top-2/3 left-0 w-[200%] h-px bg-linear-to-r from-transparent via-white/30 to-transparent transform -rotate-12 origin-left"
            style={{
              animation: "slide-reverse 25s linear infinite",
              animationDelay: "3s",
            }}
          ></div>
        </div>

        {/* Vertical lines */}
        <div
          className="absolute top-0 left-1/4 w-px h-full bg-linear-to-b from-transparent via-white/20 to-transparent animate-pulse"
          style={{ animationDelay: "0.5s" }}
        ></div>
        <div
          className="absolute top-0 left-1/2 w-px h-full bg-linear-to-b from-transparent via-white/30 to-transparent animate-pulse"
          style={{ animationDelay: "1.5s" }}
        ></div>
        <div
          className="absolute top-0 left-3/4 w-px h-full bg-linear-to-b from-transparent via-white/20 to-transparent animate-pulse"
          style={{ animationDelay: "2.5s" }}
        ></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 relative z-10 max-w-full min-w-0">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-display font-black mb-4 md:mb-6 leading-tight break-words">
              {t("landing.hero.title")}{" "}
              <span className="inline-block px-2 py-1 rounded-md bg-romance-50 text-tiber-900 mt-1 sm:mt-0">
                {t("landing.hero.titleHighlight")}
              </span>
            </h1>

            <p className="text-base font-light sm:text-lg md:text-xl text-white/90 mb-6 md:mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              {t("landing.hero.description")}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-8 md:mb-12 justify-center lg:justify-start">
              <Button
                type="button"
                size="lg"
                variant="primary"
                className="w-full sm:w-auto solid border-2 border-romance-50 text-romance-50"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.preventDefault();
                  navigate("/onboarding/select-type");
                }}
              >
                {t("landing.hero.ctaPrimary")}
              </Button>
              <Button
                size="lg"
                variant="ghost"
                className="w-full sm:w-auto text-white hover:text-tiber-900"
                onClick={() => {
                  const element = document.getElementById("servicios");
                  element?.scrollIntoView({ behavior: "smooth" });
                }}
                rightIcon={<ArrowRightIcon className="w-5 h-5" />}
              >
                {t("landing.hero.ctaSecondary")}
              </Button>
            </div>
          </div>

          {/* Right Content - Visual */}
          <div className="relative block">
            <HeroChatDemo />
          </div>
        </div>
      </div>
    </section>
  );
};
